import { isSameDay } from "date-fns";
import { OccupiedDay } from "../api/occupied-days";
import { Reservation } from "../api/reservations";
import { Table } from "../api/tables";
import { Payment } from "../api/payments";
import { format } from "./date-utility";

export const throttle = (func: Function, delay = 100, end = false) => {
	let timer: number;
	let lastInvokation: number;

	return function() {
		const context = this;
		const args = arguments;
		const elapsedTime = Date.now() - lastInvokation;

		const invoke = () => {
			func.apply(context, args);
			lastInvokation = Date.now();
		};

		if (!lastInvokation) {
			invoke();
		} else {
			clearTimeout(timer);
			timer = setTimeout(() => {
				if (end || elapsedTime >= delay) {
					invoke();
				}
			}, delay - elapsedTime);
		}
	};
};

export const getTimeFilteredReservations = (
	reservations: Reservation[],
	selectedTime: string
) => {
	let filteredReservations: Array<Reservation> = [];
	if(selectedTime && selectedTime!==null){
		reservations.forEach(function (item, index) {
			if(item.time) {
				if(format(item.time, "HH-mm") === selectedTime){
					filteredReservations.push(item); 
				}
			} else {
				filteredReservations.push(item); // Ganztägig
			}
		});
	} else {
		filteredReservations = reservations;
	}
	return filteredReservations;
};

export const notEmpty = (v: any) => {
	return v !== null && v !== undefined && v !== "";
};

export const isSafariBrowser = () => {
	if(navigator && navigator.userAgent) {
		return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
	}

	return false;
};

/**
 * TODO: This function should not be removed
 */
export const fixTime = (time: Date) => {
	if (time !== null && time.toString().substr(0, 4) === "0001") {
		return null;
	}

	return time;
};

export const convertPhoneNumberToLink = (phoneNumber: string) => {
	return `tel:${phoneNumber.replace(/[\s-\(\)\/]/g, "")}`;
};

export const getSelectedOccupiedDay = (
	occupiedDays: OccupiedDay[],
	selectedDate: Date
) => {
	return occupiedDays.filter(day => {
		return isSameDay(day.date, selectedDate);
	})[0];
};

export const getReservationsForDay = (
	reservations: Reservation[],
	selectedDate: Date
) => {
	return reservations.filter(r => isSameDay(r.date, selectedDate));
};

export const getReservationsForTable = (
	tableId: string,
	reservations: Reservation[],
	selectedDate: Date
) => {
	return reservations.filter(
		r => r.tableId === tableId && isSameDay(r.date, selectedDate)
	).sort((a, b) => {
		if (a.time && b.time) {
			return a.time.getTime() - b.time.getTime()
		}

		return -1;
	});
};

export const getPeopleCountSumForTable = (
	tableId: string,
	reservations: Reservation[],
	tableGroups: string[][]
) => {
	let tableGroupIndex = -1;

	if (tableGroups && tableGroups.length > 0) {
		tableGroupIndex = tableGroups.reduce((acc, group, index) => {
			if (group.indexOf(tableId) > -1) {
				acc = index;
			}

			return acc;
		}, -1);
	}

	const activeGroup =
		tableGroupIndex > -1 ? tableGroups[tableGroupIndex] : null;

	return reservations.reduce((acc, reservation) => {
		if (
			(activeGroup !== null &&
				activeGroup.indexOf(reservation.tableId) > -1) ||
			reservation.tableId === tableId
		) {
			acc = acc + reservation.peopleCount;
		}

		return acc;
	}, 0);
};

export const getTotalSoftLimitForTable = (
	id: string,
	tables: Table[],
	tableGroups: string[][]
) => {
	let finalSoftLimit = 0;

	if (tableGroups && tableGroups.length > 0) {
		finalSoftLimit = tableGroups.reduce((acc, group) => {
			if (group.indexOf(id) > -1) {
				acc = group.reduce((a, id) => {
					const table = tables.find(t => t.id === id);

					if (table) {
						a += table.peopleSoftLimit;
					}

					return a;
				}, 0);
			}

			return acc;
		}, 0);
	}

	if (finalSoftLimit === 0) {
		const targetTable = tables.find(t => t.id === id);

		finalSoftLimit = targetTable ? targetTable.peopleSoftLimit : 0;
	}

	return finalSoftLimit;
};

export const isAreaLocked = (areaId: string, occupiedDay: OccupiedDay) => {
	return (
		(
			occupiedDay &&
			occupiedDay.lockdown
		) || (
			occupiedDay &&
			occupiedDay.lockedAreas &&
			occupiedDay.lockedAreas.indexOf(areaId) > -1
		)
	);
};

export const coordinatesInAngleRange = (
	x1: number,
	y1: number,
	x2: number,
	y2: number,
	angleRanges: { start?: number; end?: number }[]
) => {
	const angle = (Math.atan2(y2 - y1, x2 - x1) * 180) / Math.PI + 180;

	for (let i = 0; i < angleRanges.length; i++) {
		if (
			(angleRanges[i].start == null || angle >= angleRanges[i].start) &&
			(angleRanges[i].end == null || angle <= angleRanges[i].end)
		) {
			return true;
		}
	}

	return false;
};

export const nl2br = (str: string) => {
	const r = str.replace(/\n/g, "<br>");

	return r;
};

export const downloadWithFilename = (url: string, filename: string) => {
	const anchor = document.createElement("a");
	anchor.href = url;
	anchor.download = filename;

	document.body.appendChild(anchor);
	anchor.click();
	document.body.removeChild(anchor);
};

// The server sends unset times as Go zero time ("0001-01-01T00:00:00Z")
export const isTimeSet = (time?: string | Date) => {
	if (!time) {
		return false;
	}

	return !(typeof time === "string" && time.startsWith("0001-"));
};

export const isGuaranteePayment = (payment: Payment) => {
	return payment != null && payment.type === "guarantee";
};

export const isChargeInProgress = (payment: Payment) => {
	return payment != null && (
		payment.chargeState === "open" ||
		payment.chargeState === "pending" ||
		payment.chargeState === "authorized"
	);
};

export const getPaymentCardLabel = (payment: Payment) => {
	if (payment == null || !payment.cardNumber) {
		return payment && payment.cardLabel ? payment.cardLabel : "";
	}

	return ((payment.cardLabel || "") + " •••• " + payment.cardNumber).trim();
};

// True if both payments are the same payment in the same state
export const paymentStateMatches = (a: Payment, b: Payment) => {
	if (!a || !b) {
		return !a && !b;
	}

	return a.id === b.id
		&& a.state === b.state
		&& a.chargeState === b.chargeState
		&& a.mandateStatus === b.mandateStatus;
};

// A card guarantee can be requested for a reservation without card, or if the
// previous request expired or the deposit failed
export const canRequestGuarantee = (payment: Payment) => {
	if (payment == null) {
		return true;
	}

	return isGuaranteePayment(payment) && (
		payment.state === "expired" ||
		payment.state === "failed" ||
		payment.state === "canceled"
	);
};

// Last moment (end of day) on which the no-show fee can be charged
export const getChargeDeadline = (reservationDate: string | Date, days: number) => {
	if (!isTimeSet(reservationDate)) {
		return null;
	}

	const deadline = new Date(reservationDate);
	deadline.setDate(deadline.getDate() + days);
	deadline.setHours(23, 59, 59, 999);

	return deadline;
};

// Returns why the no-show fee can not be charged (null if it can be charged).
// Mirrors the checks of the server, which has the final say.
export const getChargeBlockReason = (
	payment: Payment,
	reservationDate: string | Date,
	checkedIn: boolean,
	days: number,
	now: Date = new Date()
): string => {
	if (!isGuaranteePayment(payment)) {
		return "Keine Kartenhinterlegung";
	}

	if (payment.state !== "completed") {
		return "Kreditkarte wurde noch nicht hinterlegt";
	}

	if (payment.chargeState === "paid") {
		return "No-Show-Gebühr wurde bereits abgebucht";
	}

	if (isChargeInProgress(payment)) {
		return "Abbuchung wird verarbeitet";
	}

	if (payment.mandateStatus === "revoked") {
		return "Kreditkarte wurde bereits freigegeben";
	}

	// "pending" is checked by the server, which refreshes it from mollie first
	if (payment.mandateStatus !== "valid" && payment.mandateStatus !== "pending") {
		return "Keine gültige Kreditkarte hinterlegt";
	}

	if (!isTimeSet(payment.consentTime)) {
		return "Gast hat den Bedingungen für die vereinbarte Gebühr nicht zugestimmt";
	}

	if (checkedIn) {
		return "Gast ist eingecheckt";
	}

	const deadline = getChargeDeadline(isTimeSet(reservationDate) ? reservationDate : payment.reservationDate, days);
	if (deadline && now > deadline) {
		return "Frist zum Abbuchen ist am " + format(deadline, "DD.MM.YYYY") + " abgelaufen";
	}

	return null;
};

const getReadableGuaranteeStatus = (
	payment: Payment,
	shortResult: boolean
) => {
	const fee = getReadablePaymentCurrency(payment.currency, payment.amount);
	const card = getPaymentCardLabel(payment);

	switch (payment.state) {
		case "awaiting_payment":
			return (shortResult)
				? "Karte offen"
				: "Kreditkarte noch nicht hinterlegt (vereinbarte No-Show-Gebühr " + fee + ")";

		case "expired":
			return (shortResult)
				? "Abgelaufen"
				: "Link zur Kartenhinterlegung abgelaufen am " + format(payment.expirationDate, "DD.MM. HH:mm");

		case "pending":
			return (shortResult)
				? "Wird geprüft"
				: "Kreditkarte wird im Moment geprüft";

		case "completed":
			if (payment.chargeState === "paid") {
				const charged = getReadablePaymentCurrency(payment.currency, payment.chargeAmount);

				return (shortResult)
					? "No-Show " + charged + " abgebucht"
					: "No-Show-Gebühr " + charged + " abgebucht" + (isTimeSet(payment.chargeTime) ? " am " + format(payment.chargeTime, "DD.MM. HH:mm") : "");
			}

			if (isChargeInProgress(payment)) {
				return (shortResult)
					? "Abbuchung läuft"
					: "Abbuchung der No-Show-Gebühr wird verarbeitet";
			}

			if (payment.mandateStatus === "revoked") {
				return (shortResult)
					? "Karte freigegeben"
					: "Kreditkarte freigegeben" + (isTimeSet(payment.releaseTime) ? " am " + format(payment.releaseTime, "DD.MM. HH:mm") : "");
			}

			if (payment.chargeState === "failed" || payment.chargeState === "canceled" || payment.chargeState === "expired") {
				return (shortResult)
					? "Abbuchung fehlgeschlagen"
					: "Abbuchung fehlgeschlagen, Kreditkarte hinterlegt" + (card ? " (" + card + ")" : "");
			}

			return (shortResult)
				? "Karte hinterlegt"
				: "Kreditkarte hinterlegt" + (card ? " (" + card + ")" : "") + ", vereinbarte No-Show-Gebühr " + fee;
	}

	return "Fehlgeschlagen";
};

export const getReadablePaymentStatus = (
	payment: Payment,
	shortResult: boolean = false
) => {
	if (isGuaranteePayment(payment)) {
		return getReadableGuaranteeStatus(payment, shortResult);
	}

	if(payment != null) {
		switch (payment.state) {
			case "awaiting_payment":
				return (shortResult) 
					? "Offen (" + getReadablePaymentCurrency(payment.currency, payment.amount) + ")"
					: "Erwarte Zahlungseingang (" + getReadablePaymentCurrency(payment.currency, payment.amount) + ")";
	
			case "expired":
				return (shortResult) 
					? "Abgelaufen"
					: "Abgelaufen am " + format(payment.expirationDate, "DD.MM. HH:mm");
			
			case "pending":
				return (shortResult) 
					? "Wir verarbeitet"
					: "Zahlung wird im Moment verarbeitet";
	
			case "completed":
				return (shortResult) 
					? getReadablePaymentCurrency(payment.currency, payment.amount) + " bezahlt"
					: getReadablePaymentCurrency(payment.currency, payment.amount) + " bezahlt am " + format(payment.completionTime, "DD.MM. HH:mm");
		}
	}

	return (shortResult) 
		? "Fehlgeschlagen" 
		: "Fehlgeschlagen";
};

export const getReadablePaymentCurrency = (
	currency: string,
	amount: number
) => {
	return new Intl.NumberFormat('de-AT', {
		style: 'currency',
		currency: currency
	}).format(amount);
};

export const hasAdditionalNotes = (
	notes: string
) => {
	return (
		notes &&
		!(
			(
				notes.startsWith("Unterkunft in der Region: ") && 
				notes.indexOf("\n") == -1
			) || (
				notes.startsWith("Unterkunft in der Region: ") && 
				notes.split('\n').length == 2 && 
				(
					notes.endsWith("Kind") || 
					notes.endsWith("Kinder")
				)
			) || (
				notes.indexOf("\n") == -1 && 
				(
					notes.endsWith("Kind") || 
					notes.endsWith("Kinder")
				)
			)
		)
	) ? true : false;
};

